import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, FileCheck, ExternalLink } from "lucide-react";

import { PDFUploadCard, AIProvider } from "@/components/validation/PDFUploadCard";
import { PDFPreview } from "@/components/validation/PDFPreview";
import { PriceBookSelector, PriceBookItem } from "@/components/validation/PriceBookSelector";
import { InvoiceExtractionResults, ExtractionResult, LineItem } from "@/components/invoice/InvoiceExtractionResults";
import {
  TicketExtractionResults,
  TicketExtractionResult,
  TicketLineItem,
} from "@/components/ticket/TicketExtractionResults";
import { useAuth } from "@/contexts/AuthContext";
import { resolvePaymentTerms, calculateDiscountAmount, enrichExtractionWithTerms } from "@prolarus/shared";
import { format, parseISO, isValid } from "date-fns";
import { getDefaultStatusId } from "@/hooks/useDefaultStatus";
import { useActiveStatusId } from "@/hooks/useDefaultStatus";
import { profileService, vendorService, invoiceService, invoiceItemService, ticketService, ticketItemService, type ServiceContext } from "@/services";
import { extractService } from "@/api/extract";

interface Vendor {
  id: string;
  name: string;
}

export default function ValidateInvoice() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch user profile to get company_id
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Get active status ID for vendor filtering
  const { data: activeVendorStatus } = useActiveStatusId('vendor');

  // Fetch vendors for the company (filter by active status_id)
  const { data: vendors = [] } = useQuery({
    queryKey: ["vendors", profile?.company_id, activeVendorStatus?.id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const result = await vendorService.listActive(profile.company_id, activeVendorStatus?.id || null);
      if (result.error) throw result.error;
      return (result.data || []).map(v => ({ id: v.id, name: v.name })) as Vendor[];
    },
    enabled: !!profile?.company_id,
  });

  // Invoice state
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceAIProvider, setInvoiceAIProvider] = useState<AIProvider>("openai");
  const [invoiceProcessing, setInvoiceProcessing] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceResults, setInvoiceResults] = useState<ExtractionResult | null>(null);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [isSavingTicket, setIsSavingTicket] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [savedInvoiceId, setSavedInvoiceId] = useState<string | null>(null);

  // Ticket state
  const [ticketFile, setTicketFile] = useState<File | null>(null);
  const [ticketAIProvider, setTicketAIProvider] = useState<AIProvider>("openai");
  const [ticketProcessing, setTicketProcessing] = useState(false);
  const [ticketStatus, setTicketStatus] = useState("");
  const [ticketError, setTicketError] = useState<string | null>(null);
  const [ticketResults, setTicketResults] = useState<TicketExtractionResult | null>(null);

  // Price book state
  const [selectedPriceBookId, setSelectedPriceBookId] = useState<string | null>(null);
  const [priceBookItems, setPriceBookItems] = useState<PriceBookItem[]>([]);

  // Match invoice items to price book items when either changes
  const matchInvoiceItemsToPriceBook = useCallback((lineItems: LineItem[], pbItems: PriceBookItem[]): LineItem[] => {
    if (!pbItems.length) return lineItems;

    return lineItems.map((item) => {
      if (!item.item_code) return item;

      const match = pbItems.find((pbItem) => pbItem.itemcode.toLowerCase() === item.item_code?.toLowerCase());

      return {
        ...item,
        price_book_item_id: match?.id || undefined,
      };
    });
  }, []);

  // Match ticket items to price book items
  const matchTicketItemsToPriceBook = useCallback(
    (lineItems: TicketLineItem[], pbItems: PriceBookItem[]): TicketLineItem[] => {
      if (!pbItems.length) return lineItems;

      return lineItems.map((item) => {
        if (!item.item_code) return item;

        const match = pbItems.find((pbItem) => pbItem.itemcode.toLowerCase() === item.item_code?.toLowerCase());

        return {
          ...item,
          price_book_item_id: match?.id || undefined,
        };
      });
    },
    [],
  );

  // Re-match invoice items when price book items change
  useEffect(() => {
    if (invoiceResults && priceBookItems.length > 0) {
      const matchedItems = matchInvoiceItemsToPriceBook(invoiceResults.data.line_items, priceBookItems);

      // Only update if there are actual changes
      const hasChanges = matchedItems.some(
        (item, idx) => item.price_book_item_id !== invoiceResults.data.line_items[idx].price_book_item_id,
      );

      if (hasChanges) {
        setInvoiceResults({
          ...invoiceResults,
          data: { ...invoiceResults.data, line_items: matchedItems },
        });
      }
    }
  }, [priceBookItems, matchInvoiceItemsToPriceBook]);

  // Re-match ticket items when price book items change
  useEffect(() => {
    if (ticketResults && priceBookItems.length > 0) {
      const matchedItems = matchTicketItemsToPriceBook(ticketResults.data.line_items, priceBookItems);

      // Only update if there are actual changes
      const hasChanges = matchedItems.some(
        (item, idx) => item.price_book_item_id !== ticketResults.data.line_items[idx].price_book_item_id,
      );

      if (hasChanges) {
        setTicketResults({
          ...ticketResults,
          data: { ...ticketResults.data, line_items: matchedItems },
        });
      }
    }
  }, [priceBookItems, matchTicketItemsToPriceBook]);

  const extractInvoice = async () => {
    if (!invoiceFile) return;

    setInvoiceProcessing(true);
    setInvoiceError(null);
    setInvoiceStatus("Reading PDF file...");

    try {
      const base64 = await fileToBase64(invoiceFile);
      const providerLabel = "OpenAI";
      setInvoiceStatus(`Extracting data with ${providerLabel}...`);

      const data = await extractService.extractInvoice<ExtractionResult>({
        pdfBase64: base64,
        aiProvider: invoiceAIProvider,
      });

      if (!data.success) throw new Error(data.error || "Extraction failed");

      // Match items to price book if already selected
      let extractedResult = data;
      if (priceBookItems.length > 0) {
        const matchedItems = matchInvoiceItemsToPriceBook(extractedResult.data.line_items, priceBookItems);
        extractedResult = {
          ...extractedResult,
          data: { ...extractedResult.data, line_items: matchedItems },
        };
      }

      setInvoiceResults(enrichExtractionWithTerms(extractedResult));

      // Auto-match vendor by name
      const extractedVendorName = extractedResult.data.vendor_name?.toLowerCase().trim();
      if (extractedVendorName && vendors.length > 0) {
        const matchedVendor = vendors.find(
          (v) =>
            v.name.toLowerCase().trim() === extractedVendorName ||
            v.name.toLowerCase().includes(extractedVendorName) ||
            extractedVendorName.includes(v.name.toLowerCase()),
        );
        if (matchedVendor) {
          setSelectedVendorId(matchedVendor.id);
        }
      }
      toast({
        title: "Invoice Extraction Complete",
        description: `Successfully extracted invoice data with ${providerLabel}.`,
      });
    } catch (err) {
      console.error("Invoice extraction error:", err);
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setInvoiceError(errorMessage);
      toast({ title: "Extraction Failed", description: errorMessage, variant: "destructive" });
    } finally {
      setInvoiceProcessing(false);
      setInvoiceStatus("");
    }
  };

  const extractTicket = async () => {
    if (!ticketFile) return;

    setTicketProcessing(true);
    setTicketError(null);
    setTicketStatus("Reading PDF file...");

    try {
      const base64 = await fileToBase64(ticketFile);
      const providerLabel = "OpenAI";
      setTicketStatus(`Extracting data with ${providerLabel}...`);

      const data = await extractService.extractTicket<TicketExtractionResult>({
        pdfBase64: base64,
        aiProvider: ticketAIProvider,
      });

      if (!data.success) throw new Error(data.error || "Extraction failed");

      // Match items to price book if already selected
      let extractedResult = data;
      if (priceBookItems.length > 0) {
        const matchedItems = matchTicketItemsToPriceBook(extractedResult.data.line_items, priceBookItems);
        extractedResult = {
          ...extractedResult,
          data: { ...extractedResult.data, line_items: matchedItems },
        };
      }

      setTicketResults(extractedResult);
      toast({
        title: "Ticket Extraction Complete",
        description: `Successfully extracted ticket data with ${providerLabel}.`,
      });
    } catch (err) {
      console.error("Ticket extraction error:", err);
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setTicketError(errorMessage);
      toast({ title: "Extraction Failed", description: errorMessage, variant: "destructive" });
    } finally {
      setTicketProcessing(false);
      setTicketStatus("");
    }
  };

  const clearInvoice = () => {
    setInvoiceFile(null);
    setInvoiceResults(null);
    setInvoiceError(null);
  };

  const clearTicket = () => {
    setTicketFile(null);
    setTicketResults(null);
    setTicketError(null);
  };

  const saveInvoice = async () => {
    if (!invoiceResults || !profile?.company_id || !user?.id) {
      toast({
        title: "Cannot Save",
        description: "Missing invoice data or company profile.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingInvoice(true);
    try {
      const { data: extractedData } = invoiceResults;
      const context: ServiceContext = { companyId: profile.company_id, userId: user.id };

      // 1. Upload PDF to storage (if file exists)
      let source_file_path: string | null = null;
      let source_filename: string | null = null;

      if (invoiceFile) {
        source_filename = invoiceFile.name;
        const filePath = `${profile.company_id}/${Date.now()}_${invoiceFile.name}`;
        const { error: uploadError } = await supabase.storage.from("invoice-files").upload(filePath, invoiceFile);

        if (uploadError) {
          console.error("File upload error:", uploadError);
          // Continue without file - not a fatal error
        } else {
          source_file_path = filePath;
        }
      }

      // 2. Explicit AI-extracted due date takes precedence
      let dueDate: string | null = null;
      let discountExpiration: string | null = null;
      let discount: number | null = null;

      if (extractedData.due_date) {
        try {
          const parsed = parseISO(extractedData.due_date);
          if (isValid(parsed)) dueDate = extractedData.due_date;
        } catch { /* ignore */ }
      }

      if (extractedData.payment_terms && extractedData.invoice_date) {
        try {
          const invoiceDate = parseISO(extractedData.invoice_date);
          if (isValid(invoiceDate)) {
            const resolved = resolvePaymentTerms(extractedData.payment_terms, invoiceDate);
            discount = resolved.discountPercent || null;
            discountExpiration = format(resolved.discountExpirationDate, "yyyy-MM-dd");
            if (!dueDate) dueDate = format(resolved.dueDate, "yyyy-MM-dd");
          }
        } catch { /* terms didn't parse — OK for AI-extracted data */ }
      }

      const discountAmt = calculateDiscountAmount(extractedData.total_amount, discount);

      // 3. Get default status_id for invoice
      const defaultStatusId = await getDefaultStatusId('invoice');

      // 4. Insert invoice header using service
      const invoiceResult = await invoiceService.create(
        {
          vendor_id: selectedVendorId,
          invoice_number: extractedData.invoice_number,
          invoice_date: extractedData.invoice_date || null,
          due_date: dueDate,
          po_number: extractedData.po_number || null,
          payment_terms: extractedData.payment_terms || null,
          subtotal: extractedData.subtotal || 0,
          tax_amount: extractedData.tax_amount || 0,
          total_amount: extractedData.total_amount,
          discount: discount,
          discount_amt: discountAmt,
          discount_expiration: discountExpiration,
          currency: extractedData.currency || "USD",
          extraction_method: invoiceResults.extraction_method,
          extraction_confidence: invoiceResults.confidence,
          ai_provider: invoiceAIProvider,
          source_file_path,
          source_filename,
          status_id: defaultStatusId,
          price_book_id: selectedPriceBookId,
        },
        context
      );

      if (invoiceResult.error || !invoiceResult.data) {
        throw invoiceResult.error || new Error("Failed to create invoice");
      }

      const invoice = invoiceResult.data;

      // 5. Insert invoice items using service
      if (extractedData.line_items.length > 0) {
        const invoiceItems = extractedData.line_items.map((item) => ({
          invoice_id: invoice.id,
          sequence: item.sequence,
          item_code: item.item_code || null,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          uom: item.uom || null,
          discount: item.discount || null,
          line_total: item.line_total,
          price_book_item_id: item.price_book_item_id || null,
        }));

        const itemsResult = await invoiceItemService.createBatch(invoiceItems, context);
        if (itemsResult.error) throw itemsResult.error;
      }

      // Store the saved invoice ID for ticket linking
      setSavedInvoiceId(invoice.id);

      toast({
        title: "Invoice Saved",
        description: `Invoice ${extractedData.invoice_number || "N/A"} saved successfully.`,
      });
    } catch (err) {
      console.error("Save error:", err);
      toast({
        title: "Save Failed",
        description: err instanceof Error ? err.message : "Failed to save invoice",
        variant: "destructive",
      });
    } finally {
      setIsSavingInvoice(false);
    }
  };

  const saveTicket = async () => {
    if (!ticketResults || !profile?.company_id || !user?.id) {
      toast({
        title: "Cannot Save",
        description: "Missing ticket data or company profile.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingTicket(true);
    try {
      const { data: extractedData } = ticketResults;
      const context: ServiceContext = { companyId: profile.company_id, userId: user.id };

      // 1. Upload PDF to storage (if file exists)
      let source_file_path: string | null = null;
      let source_filename: string | null = null;

      if (ticketFile) {
        source_filename = ticketFile.name;
        const filePath = `${profile.company_id}/${Date.now()}_${ticketFile.name}`;
        const { error: uploadError } = await supabase.storage.from("ticket-files").upload(filePath, ticketFile);

        if (uploadError) {
          console.error("File upload error:", uploadError);
          // Continue without file - not a fatal error
        } else {
          source_file_path = filePath;
        }
      }

      // 2. Get default status_id for ticket
      const ticketDefaultStatusId = await getDefaultStatusId('invoice'); // tickets use same entity type for now

      // 3. Insert ticket header using service
      const ticketResult = await ticketService.create(
        {
          vendor_id: selectedVendorId,
          ticket_number: extractedData.ticket_number || null,
          ticket_date: extractedData.ticket_date || null,
          subtotal: extractedData.subtotal || 0,
          tax_amount: extractedData.tax_amount || 0,
          total_amount: extractedData.total_amount,
          currency: extractedData.currency || "USD",
          notes: extractedData.notes || null,
          extraction_method: ticketResults.extraction_method,
          extraction_confidence: ticketResults.confidence,
          ai_provider: ticketAIProvider,
          source_file_path,
          source_filename,
          status_id: ticketDefaultStatusId,
          price_book_id: selectedPriceBookId,
          invoice_id: savedInvoiceId,
        },
        context
      );

      if (ticketResult.error || !ticketResult.data) {
        throw ticketResult.error || new Error("Failed to create ticket");
      }

      const ticket = ticketResult.data;

      // 4. Insert ticket items using service
      if (extractedData.line_items.length > 0) {
        const ticketItems = extractedData.line_items.map((item) => ({
          ticket_id: ticket.id,
          sequence: item.sequence,
          item_code: item.item_code || null,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          uom: item.uom || null,
          discount: item.discount ?? null,
          line_total: item.line_total,
          price_book_item_id: item.price_book_item_id || null,
        }));

        const itemsResult = await ticketItemService.createBatch(ticketItems, context);
        if (itemsResult.error) throw itemsResult.error;
      }

      toast({
        title: "Ticket Saved",
        description: `Ticket ${extractedData.ticket_number || "N/A"} saved successfully.`,
      });
    } catch (err) {
      console.error("Save error:", err);
      toast({
        title: "Save Failed",
        description: err instanceof Error ? err.message : "Failed to save ticket",
        variant: "destructive",
      });
    } finally {
      setIsSavingTicket(false);
    }
  };

  const handlePriceBookItemsLoaded = useCallback((items: PriceBookItem[]) => {
    setPriceBookItems(items);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Validate an Invoice</h1>
          <p className="text-muted-foreground mt-1">
            Upload an invoice and field ticket to compare against a price book.
          </p>
        </div>
        {savedInvoiceId && (
          <Button
            variant="outline"
            onClick={() => navigate(`/invoices/${savedInvoiceId}`)}
            className="flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Open Invoice Detail
          </Button>
        )}
      </div>

      {/* Upload Section - Split Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Invoice Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!invoiceResults ? (
              <PDFUploadCard
                title="Upload Invoice"
                description="Upload a vendor invoice PDF"
                file={invoiceFile}
                setFile={setInvoiceFile}
                aiProvider={invoiceAIProvider}
                setAIProvider={setInvoiceAIProvider}
                isProcessing={invoiceProcessing}
                processingStatus={invoiceStatus}
                error={invoiceError}
                setError={setInvoiceError}
                onExtract={extractInvoice}
                onClear={clearInvoice}
              />
            ) : (
              <Tabs defaultValue="data" className="w-full">
                <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-4">
                  <TabsTrigger value="data" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">Extracted Data</TabsTrigger>
                  <TabsTrigger value="pdf" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">PDF Preview</TabsTrigger>
                </TabsList>
                <TabsContent value="data" className="mt-4">
                  <InvoiceExtractionResults
                    results={invoiceResults}
                    onClear={clearInvoice}
                    onSave={saveInvoice}
                    isSaving={isSavingInvoice}
                    vendors={vendors}
                    selectedVendorId={selectedVendorId}
                    onVendorChange={setSelectedVendorId}
                  />
                </TabsContent>
                <TabsContent value="pdf" className="mt-4">
                  <PDFPreview file={invoiceFile} />
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>

        {/* Field Ticket Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileCheck className="w-5 h-5" />
              Field Ticket
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!ticketResults ? (
              <PDFUploadCard
                title="Upload Field Ticket"
                description="Upload a field ticket PDF"
                file={ticketFile}
                setFile={setTicketFile}
                aiProvider={ticketAIProvider}
                setAIProvider={setTicketAIProvider}
                isProcessing={ticketProcessing}
                processingStatus={ticketStatus}
                error={ticketError}
                setError={setTicketError}
                onExtract={extractTicket}
                onClear={clearTicket}
              />
            ) : (
              <Tabs defaultValue="data" className="w-full">
                <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-4">
                  <TabsTrigger value="data" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">Extracted Data</TabsTrigger>
                  <TabsTrigger value="pdf" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">PDF Preview</TabsTrigger>
                </TabsList>
                <TabsContent value="data" className="mt-4">
                  <TicketExtractionResults
                    results={ticketResults}
                    onClear={clearTicket}
                    onSave={saveTicket}
                    isSaving={isSavingTicket}
                  />
                </TabsContent>
                <TabsContent value="pdf" className="mt-4">
                  <PDFPreview file={ticketFile} />
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Price Book Selector */}
      <PriceBookSelector
        selectedPriceBookId={selectedPriceBookId}
        onSelectPriceBook={setSelectedPriceBookId}
        onPriceBookItemsLoaded={handlePriceBookItemsLoaded}
      />
    </div>
  );
}

// Helper function
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
